/* Product tour overlay (prototype TOUR/tourLayer): highlights shell areas by data-testid,
 * Next/Back/Skip, Escape closes, arrow keys navigate, positions recomputed on resize/scroll. */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n";
import { Button } from "../../ui";
import type { Pair } from "./guides";
import "./help.css";

export interface TourStep { sel: string; t: Pair; d: Pair }

export const TOUR: TourStep[] = [
  { sel: "[data-testid=sidebar], [data-testid=tabbar]", t: ["Navigation", "التنقل"], d: ["Every module lives here, grouped by People, Time, Pay, Compliance and Company. Badges show what needs you. On phones use the bar at the bottom and ‘More’.", "كل الوحدات هنا مجمعة حسب الفئات، والشارات توضح ما يحتاجك. على الجوال استخدم الشريط السفلي و«المزيد»."] },
  { sel: "[data-testid=cmd-open]", t: ["Search anything", "ابحث عن أي شيء"], d: ["Find employees, pages and actions. Shortcut: Ctrl+K.", "ابحث عن الموظفين والصفحات والإجراءات. الاختصار Ctrl+K."] },
  { sel: "[data-testid=assistant-open]", t: ["AI assistant", "المساعد الذكي"], d: ["Ask Labor Law questions or ‘EOSB for Khalid’. Shortcut: Ctrl+J.", "اسأل عن نظام العمل أو «مكافأة خالد». الاختصار Ctrl+J."] },
  { sel: "[data-testid=help-open]", t: ["Help on every page", "مساعدة في كل صفحة"], d: ["Open step-by-step guides for the page you're on, search all guides or restart this tour.", "افتح الأدلة خطوة بخطوة للصفحة الحالية أو ابحث في كل الأدلة أو أعد الجولة."] },
  { sel: "[data-testid=role-select]", t: ["Switch view", "تبديل العرض"], d: ["See the app as the owner, HR, accountant, employee or StartHR admin.", "شاهد التطبيق كمالك أو موارد بشرية أو محاسب أو موظف أو مسؤول ستارت HR."] },
  { sel: "[data-testid=lang-toggle]", t: ["العربية / English", "العربية / English"], d: ["Switch the whole app between Arabic and English. Documents can still print bilingual.", "بدّل التطبيق بالكامل بين العربية والإنجليزية، ويمكن طباعة المستندات بلغتين."] },
  { sel: "[data-testid=notifs-open]", t: ["Notifications", "الإشعارات"], d: ["Approvals, expiring documents, device alerts and payroll reminders arrive here.", "تصل هنا الموافقات والوثائق القريبة من الانتهاء وتنبيهات الأجهزة وتذكيرات الرواتب."] },
  { sel: "[data-testid=page-guide], [data-testid=guide-show]", t: ["Page guides", "أدلة الصفحات"], d: ["Each page starts with a short how-to. Collapse or hide it once you know the way.", "كل صفحة تبدأ بشرح مختصر، يمكنك طيه أو إخفاؤه لاحقًا."] },
  { sel: "[data-testid=user-menu]", t: ["Your account", "حسابك"], d: ["Switch demo role, restart the tour or sign out.", "بدّل دور العرض أو أعد الجولة أو سجّل الخروج."] },
];

export const TOUR_STORE = "rukn-toured";
const CARD_W = 320, PAD = 6, GAP = 12, CARD_H = 200;

const visible = (el: Element) => { const h = el as HTMLElement; return !!(h.offsetWidth || h.offsetHeight || h.getClientRects().length); };
function findTarget(sel: string): Element | null {
  for (const part of sel.split(",")) { const el = document.querySelector(part.trim()); if (el && visible(el)) return el; }
  return null;
}
const exists = (sel: string) => sel.split(",").some((s) => !!document.querySelector(s.trim()));

interface Box { x: number; y: number; w: number; h: number; found: boolean }

/** Card + highlight positions, clamped inside the viewport (works down to 320px wide). */
export function placeTour(r: DOMRect | null, vw: number, vh: number): { hole: Box; card: { left: number; top: number; width: number } } {
  const width = Math.min(CARD_W, vw - 24);
  if (!r) return { hole: { x: vw / 2 - 100, y: 120, w: 200, h: 40, found: false }, card: { left: Math.max(12, (vw - width) / 2), top: Math.max(12, Math.min(vh - CARD_H - 12, vh / 2 - CARD_H / 2)), width } };
  const x = r.left - PAD, y = r.top - PAD, w = r.width + PAD * 2, h = r.height + PAD * 2;
  const below = y + h + CARD_H + GAP < vh;
  const left = Math.max(12, Math.min(vw - width - 12, x));
  const top = below ? y + h + GAP : Math.max(12, y - CARD_H - GAP + 10);
  return { hole: { x, y, w, h, found: true }, card: { left, top, width } };
}

export default function Tour({ onClose }: { onClose: () => void }) {
  const { L, t } = useI18n();
  const titleId = useId();
  const [steps, setSteps] = useState<TourStep[]>(TOUR);
  useLayoutEffect(() => { const s = TOUR.filter((x) => exists(x.sel)); if (s.length) setSteps(s); }, []);
  const [i, setI] = useState(0);
  const [, setTick] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const st = steps[Math.min(i, steps.length - 1)];
  const last = i >= steps.length - 1;

  const end = useCallback(() => { try { localStorage.setItem(TOUR_STORE, "1"); } catch { /* ignore */ } onClose(); }, [onClose]);
  const go = useCallback((n: number) => setI(Math.max(0, Math.min(steps.length - 1, n))), [steps.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); end(); }
      else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const fwd = (e.key === "ArrowRight") !== (document.documentElement.dir === "rtl");
        setI((x) => Math.max(0, Math.min(steps.length - 1, x + (fwd ? 1 : -1))));
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [end, steps.length]);

  useEffect(() => {
    const re = () => setTick((x) => x + 1);
    window.addEventListener("resize", re);
    window.addEventListener("scroll", re, true);
    return () => { window.removeEventListener("resize", re); window.removeEventListener("scroll", re, true); };
  }, []);

  const target = findTarget(st.sel);
  useLayoutEffect(() => {
    const el = findTarget(st.sel) as HTMLElement | null;
    el?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    cardRef.current?.querySelector<HTMLButtonElement>("[data-testid=tour-next]")?.focus({ preventScroll: true });
    setTick((x) => x + 1);
  }, [st.sel]);

  const vw = typeof window !== "undefined" ? window.innerWidth : 1024, vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const pos = placeTour(target ? target.getBoundingClientRect() : null, vw, vh);

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid="tour">
      <div className={"tour-hole" + (pos.hole.found ? "" : " none")} style={{ left: pos.hole.x, top: pos.hole.y, width: pos.hole.w, height: pos.hole.h }} data-testid="tour-hole" />
      <div ref={cardRef} className="tour-card" style={{ left: pos.card.left, top: pos.card.top, width: pos.card.width }} data-testid="tour-card">
        <div className="tour-dots" aria-hidden="true">{steps.map((_, k) => <i key={k} className={k === i ? "on" : ""} />)}</div>
        <div className="xs muted" data-testid="tour-count">{i + 1} / {steps.length}</div>
        <b id={titleId}>{L(st.t[0], st.t[1])}</b>
        <p className="sm" style={{ margin: "6px 0 12px" }}>{L(st.d[0], st.d[1])}</p>
        <div className="row between">
          <Button variant="ghost sm" onClick={end} data-testid="tour-skip">{L("Skip", "تخطي")}</Button>
          <div className="row">
            {i > 0 && <Button variant="sm" onClick={() => go(i - 1)} data-testid="tour-back">{t("Back")}</Button>}
            <Button variant="pri sm" onClick={() => (last ? end() : go(i + 1))} data-testid="tour-next">{last ? t("Done") : t("Next")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
